import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag } from "lucide-react";
import QuantityInput from "@/components/QuantityInput";
import BulkNudge from "@/components/BulkNudge";
import { usePricing } from "@/hooks/usePricing";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQty, subtotal } = useCart();
  // Quoted against the home market, as the cart page does — there is no address
  // yet, and this is a prompt rather than a charge.
  const { quote } = usePricing(items, "Singapore");

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="cart-drawer-overlay"
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex-1 bg-[#281a12]/60 backdrop-blur-sm"
        onClick={closeCart}
        data-testid="cart-drawer-backdrop"
      />
      <aside
        data-testid="cart-drawer"
        className="w-full sm:w-[440px] bg-[#FDFBF7] h-full flex flex-col aa-slide-in shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#6e5022]/20">
          <div className="font-serif-display text-2xl">Your Cart</div>
          <button
            onClick={closeCart}
            data-testid="cart-drawer-close"
            className="p-2 hover:text-[#6e5022]"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="cart-empty-state">
            <div className="w-16 h-16 border border-[#6e5022]/40 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="w-6 h-6 text-[#6e5022]" strokeWidth={1.2} />
            </div>
            <div className="font-serif-display text-xl mb-2">Your cart is empty</div>
            <p className="text-sm text-[#4A3728] mb-6">
              Discover books and heritage collectibles crafted with intention.
            </p>
            <Link
              to="/shop"
              onClick={closeCart}
              data-testid="cart-empty-shop-link"
              className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Browse Shop
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <Link to={`/product/${item.id}`} onClick={closeCart} className="block w-20 h-24 bg-[#F4F1EA] overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.title} className="w-full h-full object-contain p-1" />
                  </Link>
                  <div className="flex-1 flex flex-col">
                    <Link
                      to={`/product/${item.id}`}
                      onClick={closeCart}
                      data-testid={`cart-item-title-${item.id}`}
                      className="font-serif-display text-lg leading-tight hover:text-[#6e5022]"
                    >
                      {item.title}
                    </Link>
                    <div className="text-xs text-[#4A3728] mt-0.5">SGD {item.price.toFixed(2)}</div>

                    <div className="mt-auto flex items-center justify-between pt-3">
                      <QuantityInput
                        value={item.qty}
                        onChange={(n) => updateQty(item.id, n)}
                        size="sm"
                        testId={`cart-qty-${item.id}`}
                        label={`Quantity of ${item.title}`}
                      />
                      <button
                        onClick={() => removeItem(item.id)}
                        data-testid={`cart-remove-${item.id}`}
                        className="text-xs uppercase tracking-[0.15em] text-[#946f36] hover:text-[#6e5022]"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#6e5022]/20 px-6 py-5">
              <div className="flex items-center justify-between mb-1">
                <span className="overline text-[#4A3728]">Subtotal</span>
                <span className="font-serif-display text-2xl" data-testid="cart-subtotal">
                  SGD {subtotal.toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-[#4A3728] mb-3">
                Delivery and any bulk discount are calculated at checkout.
              </p>
              <BulkNudge quote={quote} testId="drawer-nudge" className="mb-5" />
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/cart"
                  onClick={closeCart}
                  data-testid="cart-view-link"
                  className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-4 py-3.5 text-xs uppercase tracking-[0.18em] text-center transition-colors"
                >
                  View Cart
                </Link>
                <Link
                  to="/checkout"
                  onClick={closeCart}
                  data-testid="cart-checkout-link"
                  className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-4 py-3.5 text-xs uppercase tracking-[0.18em] text-center transition-colors"
                >
                  Checkout
                </Link>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
